import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  inject,
  DestroyRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSave, faFire } from '@fortawesome/free-solid-svg-icons';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';

import { StudentProfile } from '../../../api/models/student-profile';
import { StudentProfileService } from '../../../api/services/student-profile.service';
import { ModalService } from '../../../shared/services/modal.service';
import {
  AlertService,
  alertMessages,
} from '../../../shared/services/alert.service';
import { ProfileTabCardComponent } from './profile-tab-card.component';

@Component({
  selector: 'app-student-matchtest-tab',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    FontAwesomeModule,
    ProfileTabCardComponent,
  ],
  template: `
    <app-profile-tab-card
      title="Match test"
      [icon]="faFire"
      iconColorClass="text-danger"
    >
      <button
        tabAction
        type="button"
        class="inline-flex items-center gap-1.5 rounded-full bg-mty-primary px-4 py-2 text-sm font-medium text-white hover:bg-mty-primary/90 transition-colors"
        (click)="updateMatchtest()"
      >
        <fa-icon [icon]="faSave" class="text-[10px]"></fa-icon>
        <span>{{ 'Save' | translate }}</span>
      </button>

      <p class="mb-5 text-sm text-mty-body/80">
        {{ 'Fill in the questions' | translate }}
      </p>

      <div class="space-y-6">
        @for (question of matchtest(); track $index; let key = $index) {
          <div>
            <p class="mb-3 font-medium text-mty-body">
              <span class="text-mty-body/50 mr-1">{{ key + 1 }}.</span>
              {{ question.question | translate }}
            </p>
            <input
              type="range"
              min="1"
              max="5"
              step="1"
              class="w-full accent-mty-primary"
              [ngModel]="matchtest()[key]['selectedAnswerIndex']"
              (ngModelChange)="updateMatchtestAnswer(key, $event)"
            />
            <div
              class="mt-2 flex items-stretch justify-between gap-2 text-xs text-mty-body/70"
            >
              @for (answerIndex of matchtestAnswersLength; track answerIndex) {
                <div class="flex-1 text-center">
                  {{ question['answer' + answerIndex] | translate }}
                </div>
              }
            </div>
          </div>
        }
      </div>

      <div class="mt-6 flex justify-end">
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-full bg-mty-primary px-4 py-2 text-sm font-medium text-white hover:bg-mty-primary/90 transition-colors"
          (click)="updateMatchtest()"
        >
          <fa-icon [icon]="faSave" class="text-[10px]"></fa-icon>
          <span>{{ 'Save' | translate }}</span>
        </button>
      </div>
    </app-profile-tab-card>
  `,
})
export class StudentMatchtestTabComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly modal = inject(ModalService);
  private readonly alertService = inject(AlertService);
  private readonly studentProfileService = inject(StudentProfileService);

  entity = input.required<StudentProfile>();
  matchtest = input.required<any[]>();
  tabChanged = output<string>();
  matchtestLoaded = output<any[]>();

  protected readonly faSave = faSave;
  protected readonly faFire = faFire;
  protected readonly matchtestAnswersLength = Array(5)
    .fill(0)
    .map((_, i) => i + 1);

  updateMatchtestAnswer(index: number, value: any): void {
    const updated = this.matchtest().map((item, i) =>
      i === index ? { ...item, selectedAnswerIndex: value } : item
    );
    this.matchtestLoaded.emit(updated);
  }

  updateMatchtest() {
    if (!this.entity().id) return;

    this.studentProfileService
      .updateStudentProfileMatchTestItems({
        profileId: this.entity().id!,
        body: this.matchtest(),
      })
      .pipe(
        catchError(() => {
          this.modal.Alert('Error', 'Something went wrong, please try again');
          return of(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.alertService.success(alertMessages.successDataSaved);
        this.modal.Loader();
        setTimeout(() => {
          this.modal.Loader(false);
          this.tabChanged.emit('matches-company');
          window.scrollTo(0, 0);
        }, 3000);
      });
  }
}
